/**
 * UninvoicedWidget — completed work that has not been invoiced yet. Shows the
 * total euro amount tied up (a direct cash-flow signal for management) plus the
 * top jobs, each clickable through to invoicing.
 *
 * Title / "go to uninvoiced" open the work-order list filtered to the
 * uninvoiced statuses (#2903). A row opens that work order's detail — the
 * detail page has no URL param that auto-opens invoicing.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import type { UninvoicedSummary } from '@/hooks/useDashboard';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';

/**
 * Statuses the "open uninvoiced" link filters by (#1186, #2532).
 *
 * `on_consolidated_invoice` stays off this list (#3941). That work order is
 * already attached to a collecting consolidated invoice, so it sits in the
 * billing pipeline rather than in forgotten completed work. The widget figures
 * come from the dashboard API; this constant only chooses the list filter the
 * title and the open-all action navigate to.
 */
export const UNINVOICED_STATUSES = ['work_completed', 'ready_to_invoice'] as const;

export const UNINVOICED_WORK_ORDERS_PATH = `/workorders?status=${UNINVOICED_STATUSES.join(',')}`;

interface UninvoicedWidgetProps {
  data?: UninvoicedSummary;
}

function euros(cents: number): string {
  return `€${(cents / 100).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function UninvoicedWidget({ data }: UninvoicedWidgetProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();

  const count = data?.count ?? 0;
  const totalCents = data?.totalCents ?? 0;
  const items = data?.items ?? [];
  const goToUninvoiced = () => navigate(UNINVOICED_WORK_ORDERS_PATH);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ReceiptLongIcon color="warning" />
          <Typography
            variant="h6"
            onClick={goToUninvoiced}
            sx={{ cursor: 'pointer' }}
          >
            {t('uninvoiced.title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color: 'warning.main' }}>
            {euros(totalCents)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('uninvoiced.count', { count })}
          </Typography>
        </Box>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('uninvoiced.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.bookingId}
                selected={item.bookingId === viewedWorkOrderId}
                onClick={(e) => openWorkOrder(item.bookingId, e)}
                sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.vehicleRegNumber || item.customerName}
                      </Typography>
                      {item.vehicleRegNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {item.customerName}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Typography variant="body2" fontWeight={600}>
                  {euros(item.totalCents)}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={goToUninvoiced}
          >
            {t('uninvoiced.openAll')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
